import {useState} from "react";
import {Error, MutationDeleteFaxArgs, ReferenceRetarget} from "@/api";
import {useDispatch} from "react-redux";
import {MutationFunction, useMutation} from "@apollo/client";
import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {useNavigate, NavigateFunction} from "react-router";
import {DeleteFaxMutation, DeleteFaxMutationVariables, DeleteFaxDocument} from "@/components/fax/mutations.generated";

const initialState: MutationDeleteFaxArgs = {
    id: null,
    relatedData: {
        users: [],
        ivrs: [],
    },
};

export enum ReferenceType {
    USER = "users",
    IVR = "ivrs",
}

type Selection = {
    [id: number]: number;
};

export default class DeleteFaxForm {
    domainId: number;
    faxId: number;
    values: MutationDeleteFaxArgs;
    errors: Error[];
    selections: Selection;

    protected _setValues: (data: MutationDeleteFaxArgs) => void;
    protected setErrors: (errors: Error[]) => void;
    protected setSelections: (selections: Selection) => void;
    protected dispatch: any;
    protected mutation: MutationFunction<DeleteFaxMutation, DeleteFaxMutationVariables>;
    protected navigate: NavigateFunction;

    constructor(domainId: number, faxId: number) {
        [this.mutation] = useMutation<DeleteFaxMutation, DeleteFaxMutationVariables>(DeleteFaxDocument);
        [this.errors, this.setErrors] = useState([]);
        [this.values, this._setValues] = useState(initialState);
        [this.selections, this.setSelections] = useState<Selection>({});
        this.domainId = domainId;
        this.faxId = faxId;
        this.dispatch = useDispatch();
        this.navigate = useNavigate();
    }

    redirect() {
        this.navigate(`/domains/${this.domainId}/faxes`);
    }

    updateValues(data: Partial<MutationDeleteFaxArgs>) {
        this._setValues(Object.assign({}, this.values, data));
    }

    selectNewTarget(referrerId: number, type: ReferenceType, newTargetId: number) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const found = list.find((e) => e.referrerId == referrerId);
        if (found) found.newTargetNumberId = newTargetId;
        else
            list.push({
                referrerId: referrerId,
                newTargetNumberId: newTargetId,
            });
        this._setValues(Object.assign({}, this.values, this.values));
    }

    deselectTarget(referrerId: number, type: ReferenceType) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const index = list.findIndex((e) => e.referrerId == referrerId);
        if (index > -1) list.splice(index, 1);
    }

    handleChange(newTarget: number, dataId: number, dataType: ReferenceType): void {
        if (newTarget === 0) {
            this.deselectTarget(dataId, dataType);
        } else {
            this.selectNewTarget(dataId, dataType, newTarget);
        }
        this.setSelections({
            ...this.selections,
            [dataId]: newTarget,
        });
    }

    delete() {
        this.mutation({
            variables: {
                id: this.faxId,
                relatedData: this.values.relatedData,
            },
        })
            .then((result) => {
                if (!result.data.deleteFax.errors) {
                    this.dispatch(
                        showNotification(defineMessage({id: "fax_deleted", defaultMessage: "Fax deleted."}), "success"),
                    );
                    this.redirect();
                    return;
                } else {
                    this.dispatch(
                        showNotification(
                            defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                            "error",
                        ),
                    );
                    this.setErrors(result.data.deleteFax.errors);
                    return;
                }
            })
            .catch(() => {
                this.dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            });
    }

    reset() {
        this.updateValues(initialState);
        this.setSelections({});
    }
}
